// The connection's 3D view. The interactive scene is react-three-fiber (like the lock calculator): OrbitControls,
// an orthographic camera and the HTML toolbar below the canvas. The report images still come from the imperative
// viewer (tc3dViewer), mounted off screen and built from the same scene builder when a capture starts.
//
// The card around it (title, "Mostra quote", branch, collapse) belongs to the page. When the card is collapsed keep
// this mounted (hidden with CSS): the report captures need the off-screen viewer.
import cx from "classnames";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type FC, type Ref } from "react";
import useI18n from "../../../hooks/useTranslations";
import type { CalculationConnectionType } from "../../../http/screwConnector/types/common";
import type { CalcState } from "../../../http/screwConnector/formulas/state";
import { buildScrewScene, updateScene, type BuiltScrewScene, type ElementMode } from "./view3dScene";
import { captureReportImages, type ReportImages } from "./view3dCapture";
import { element1Swatch, type ScrewConnector3DLabels } from "./view3dLabels";
import { useView3DLabels } from "./labels";
import styles from "./ScrewConnector3DView.module.css";
import { createTC3DViewer, type TC3DViewer, type ViewName } from "./tc3dViewer";
import ScrewScene, { type ViewPresetRequest } from "./3d/ScrewScene";

export type ScrewConnector3DViewHandle = {
  // Report images (cover, 3D, front and side views) with the legibility warnings. null when the view is not mounted.
  captureReportImages(): Promise<ReportImages | null>;
};

export type ScrewConnector3DViewProps = {
  state: CalcState | null;
  connectionType: CalculationConnectionType;
  // "Mostra quote": dimensions drawn and elements semi-transparent
  showQuote: boolean;
  // the engine's blockReason: the scene shows the elements only
  blocked?: boolean | string | null;
  className?: string;
  height?: number;
  // overrides of the translated hint/legend texts
  labels?: Partial<ScrewConnector3DLabels>;
  ref?: Ref<ScrewConnector3DViewHandle>;
};

const VIEWS: readonly ViewName[] = ["iso", "front", "top", "side"];

const ScrewConnector3DView: FC<ScrewConnector3DViewProps> = ({
  state,
  connectionType,
  showQuote,
  blocked,
  className,
  height = 480,
  labels,
  ref,
}) => {
  const t = useI18n().translation.screwConnectorModule.calculation.view;
  // the captures read the texts of the language current when they run
  const textsRef = useRef(t);
  textsRef.current = t;
  // The elements start solid, each in its own colour, as in the old viewer; the Elements menu can turn each one
  // semi-transparent or hide it.
  const [element1Mode, setElement1Mode] = useState<ElementMode>("solid");
  const [element2Mode, setElement2Mode] = useState<ElementMode>("solid");
  const [showScrews, setShowScrews] = useState(true);
  const [showForces, setShowForces] = useState(true);
  const [showGrid, setShowGrid] = useState(true);

  // The report viewer is fed only when a capture starts: its scene is otherwise dead weight, and rebuilding it on every
  // keystroke would duplicate the R3F scene construction. The Elements menu only changes this view: the report always
  // shows the whole connection.
  const captureStateRef = useRef({ state, showQuote, blocked });
  captureStateRef.current = { state, showQuote, blocked };

  const [viewPreset, setViewPreset] = useState<ViewPresetRequest | null>(null);

  const requestView = useCallback((view: ViewName) => {
    setViewPreset((previous) => ({ view, seq: (previous?.seq ?? 0) + 1 }));
  }, []);

  const built = useMemo(
    () => (state ? buildScrewScene(state, { showQuote, blocked: !!blocked }) : null),
    [state, showQuote, blocked],
  );
  // While the user clears a numeric field the state temporarily contains NaN. The report viewer keeps its previous
  // frame, so the interactive one keeps the last valid scene too; a blocked calculation or an empty state clears it.
  const lastValidBuild = useRef<BuiltScrewScene | null>(null);
  const build = built ?? (!state || blocked ? null : lastValidBuild.current);
  // Keep the fallback aligned with what was actually committed. With a blocked calculation (or no state) the old
  // scene is dropped; with temporary NaN inputs the last committed scene stays on screen.
  useEffect(() => {
    if (built) lastValidBuild.current = built;
    else if (!state || blocked) lastValidBuild.current = null;
  }, [built, state, blocked]);

  // Free the geometries, materials and grain textures of the replaced scene.
  useEffect(() => () => build?.dispose(), [build]);

  // ---- report capture viewer (imperative, off screen) ----
  const captureHostRef = useRef<HTMLDivElement>(null);
  const captureViewerRef = useRef<TC3DViewer | null>(null);
  const captureRunningRef = useRef(false);

  // The report viewer (a second WebGL context) is created only on the first capture, not on page load.
  useEffect(
    () => () => {
      captureViewerRef.current?.dispose();
      captureViewerRef.current = null;
    },
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      async captureReportImages() {
        const host = captureHostRef.current;
        if (!host) return null;
        const viewer = captureViewerRef.current ?? createTC3DViewer(host);
        captureViewerRef.current = viewer;
        // A second request while the images are being rendered must not rebuild the scene under the camera: it gets
        // the running capture's result.
        if (captureRunningRef.current) return captureReportImages(viewer, textsRef.current);
        captureRunningRef.current = true;
        try {
          const current = captureStateRef.current;
          updateScene(viewer, current.state, { showQuote: current.showQuote, blocked: !!current.blocked });
          return await captureReportImages(viewer, textsRef.current);
        } finally {
          captureRunningRef.current = false;
        }
      },
    }),
    [],
  );

  const legend = useView3DLabels(connectionType);
  const text = { ...legend, ...labels };
  const visibility = useMemo(
    () => ({ element1: element1Mode, element2: element2Mode, screws: showScrews, forces: showForces, grid: showGrid }),
    [element1Mode, element2Mode, showScrews, showForces, showGrid],
  );
  const elementModeTitles = {
    solid: t.toolbar.elements.solid,
    ghost: t.toolbar.elements.ghost,
    hidden: t.toolbar.elements.hidden,
  };

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.viewer}>
        <div className={styles.viewportHost} style={{ height }}>
          <ScrewScene
            build={build}
            visibility={visibility}
            showQuote={showQuote}
            viewPreset={viewPreset}
            autoFrameKey={connectionType}
          />
        </div>

        <div className={styles.toolbar}>
          <div className={styles.controls}>
            <div className={styles.menu}>
              <button type="button" className={styles.btnText}>
                {t.toolbar.menuView} ▾
              </button>
              <div className={styles.dropdown}>
                {VIEWS.map((view) => (
                  <button key={view} type="button" onClick={() => requestView(view)}>
                    {t.toolbar.views[view]}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.menu}>
              <button type="button" className={styles.btnText}>
                {t.toolbar.menuElements} ▾
              </button>
              <div className={cx(styles.dropdown, styles.elementsDropdown)}>
                <ElementModeRow
                  label={text.menuElement1}
                  value={element1Mode}
                  onChange={setElement1Mode}
                  titles={elementModeTitles}
                />
                <ElementModeRow
                  label={text.menuElement2}
                  value={element2Mode}
                  onChange={setElement2Mode}
                  titles={elementModeTitles}
                />
                <ToggleRow label={t.toolbar.elements.screws} checked={showScrews} onChange={setShowScrews} />
                <ToggleRow label={t.toolbar.elements.forces} checked={showForces} onChange={setShowForces} />
                <ToggleRow label={t.toolbar.elements.grid} checked={showGrid} onChange={setShowGrid} />
              </div>
            </div>
            <button
              type="button"
              className={styles.btn}
              title={t.toolbar.resetView}
              aria-label={t.toolbar.resetView}
              onClick={() => requestView("iso")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12a9 9 0 019-9 9.75 9.75 0 016.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 01-9 9 9.75 9.75 0 01-6.74-2.74L3 16" />
                <path d="M3 21v-5h5" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div ref={captureHostRef} className={styles.captureHost} aria-hidden="true" inert />

      <div className={styles.hint}>
        {text.hintControls} <span>{text.hintSubject}</span> {text.hintTail}
      </div>
      <div className={styles.legend}>
        <span className={styles.item}>
          <span className={cx(styles.swatch, styles.swatchElement2)} />
          <span>{text.element2}</span>
        </span>
        <span className={styles.item}>
          <span className={styles.swatch} style={element1Swatch(connectionType)} />
          <span>{text.element1}</span>
        </span>
        <span className={styles.item}>
          <span className={cx(styles.dot, styles.dotOk)} />
          {text.screwOk}
        </span>
        <span className={styles.item}>
          <span className={cx(styles.dot, styles.dotBad)} />
          {text.screwBad}
        </span>
        <span className={styles.item}>
          <span className={cx(styles.ln, styles.lnDims)} />
          {text.dims}
        </span>
        <span className={styles.item}>
          <span className={cx(styles.ln, styles.lnFn)} />
          {text.fn}
        </span>
        <span className={styles.item}>
          <span className={cx(styles.ln, styles.lnFv)} />
          {text.fv}
        </span>
        <span className={styles.item}>
          <span className={cx(styles.ln, styles.lnFa)} />
          {text.fa}
        </span>
      </div>
    </div>
  );
};

const ELEMENT_MODES: ReadonlyArray<{ mode: ElementMode; short: string }> = [
  { mode: "solid", short: "■" },
  { mode: "ghost", short: "◧" },
  { mode: "hidden", short: "□" },
];

function ElementModeRow({
  label,
  value,
  onChange,
  titles,
}: {
  label: string;
  value: ElementMode;
  onChange: (next: ElementMode) => void;
  titles: Record<ElementMode, string>;
}) {
  return (
    <div className={styles.elementRow}>
      <span className={styles.elementLabel}>{label}</span>
      <span className={styles.elementModes}>
        {ELEMENT_MODES.map((option) => (
          <button
            key={option.mode}
            type="button"
            title={titles[option.mode]}
            aria-pressed={value === option.mode}
            className={cx(styles.elementMode, value === option.mode && styles.elementModeActive)}
            onClick={() => onChange(option.mode)}
          >
            {option.short}
          </button>
        ))}
      </span>
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className={styles.elementRow}>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className={styles.elementLabel}>{label}</span>
    </label>
  );
}

export default ScrewConnector3DView;
